import React, { useCallback } from "react";
import { withFormik } from 'formik';
import * as Yup from "yup";
import { jwtDecode } from "jwt-decode";

import { Input } from "stories/forms";
import { Button, Link, Typography } from "stories/components"
import { useNavigate } from 'react-router-dom';
import ytmLogo from 'assets/img/brand/ytm.jpg';

import { httpLogin } from "services/auth"
import { httpSendActivationEmail } from "services/users"
import { logoutWhenTokenExp } from "services/logoutTokenExp";
import './LoginForm.scss';

const LoginForm = (props) => {
  const {
    values,
    touched,
    errors,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    setErrors,
    setSubmitting,
    validateForm,
  } = props;

  const getFormikInputProps = useCallback(
    name => ({
      name,
      value: values[name],
      error: Boolean(touched[name] && errors[name]),
      errorText: touched[name] ? errors[name] : "",
      onBlur: handleBlur,
      onChange: handleChange
    }),
    [values, errors, touched, handleBlur, handleChange]
  );

  const onSubmit = (e) => {
    e.preventDefault();
    validateForm().then(() => handleSubmit())
  }

  return (
    <div className="login-page-custom">
      <div className="login-logo">
        <img src={ytmLogo} alt="YTM logo" />
      </div>
      <div className="login-title-custom">Kirjaudu</div>
      <form onSubmit={onSubmit} className="login-form-custom">
        <div className="login-label-custom">Käyttäjätunnus</div>
        <Input
          {...getFormikInputProps("email")}
          placeholder=""
          type="text"
          required
          autoComplete="username"
          className="login-input-custom"
        />
        <div className="login-label-custom">Salasana</div>
        <Input
          {...getFormikInputProps("password")}
          placeholder=""
          type="password"
          required
          autoComplete="current-password"
          className="login-password-input-custom"
        />
        <div className="login-forgot-container">
          <Link className="login-forgot-link-custom" to="/auth/password/forgot">
            Salasana unohtunut
          </Link>
        </div>
        {Boolean(errors["general"]) && (
          <div className="text-center mb-3">
            <Typography className="text-danger">{errors["general"]}</Typography>
          </div>
        )}
        <Button
          name="login_button"
          variant="primary"
          disabled={isSubmitting || !values.email || !values.password}
          type="submit"
          className="login-button-custom"
        >
          Kirjaudu
        </Button>
      </form>
    </div>
  );
}

const defaultValues = {
  email: '',
  password: '',
}

const LoginFormWithFormik = withFormik({
  validateOnMount: true,
  mapPropsToValues: props => {
    const { preSetValues } = props;
    if (preSetValues) {
      return preSetValues;
    } else {
      return defaultValues
    }
  },
  validationSchema: props => {
    const {t} = props;
    const required = t('required_field','Kenttä on pakollinen');
    
    return Yup.object().shape({
      email: Yup.string().trim().required(required),
      password: Yup.string().required(required),
    });
  },
  handleSubmit: (values, { setSubmitting, setErrors, props }) => {
    const { t, navigate } = props;
    console.log("Submitting login form...");
    
    httpLogin(values?.email, values?.password)
      .then(res => {
        setSubmitting(false);
        
        if (res?.status === 200 && res?.data) {
          let { access, refresh } = res.data;
          localStorage.setItem("accessToken", access);
          localStorage.setItem("refreshToken", refresh);

          logoutWhenTokenExp();
          
          console.log("Login successful, calling handleResolve");
          props.handleResolve();
          if (props.navigate) {
            props.navigate('/');
          }
        }
        else {
          console.error("Login failed with status:", res?.status);
          if (res?.data?.errorCode && res?.data?.errorCode[0] === "USER_NOT_ACTIVATED") {
            setErrors({
              general: t('login_validation_user_not_activated','Käyttäjä ei ole aktivoitu'),
              USER_NOT_ACTIVATED: true
            })
          }
          else {
            setErrors({
              general: t('login_validation_wrong_username_password','Väärä käyttäjätunnus tai salasana'),
              ...res?.data,
            })
          }
        }
      })
      .catch(error => {
        console.error("Login error:", error?.message);
        setSubmitting(false);
        
        if (!error || !error.data) {
          setErrors({general: t('general_error','Virhe. Ota tarvittaessa yhteyttä asiakaspalveluun')});
          return;
        }
        
        const errorData = error.data;
        const errorCode = errorData?.errorCode?.[0] || '';
        
        if (errorCode === 'USER_NOT_ACTIVATED') {
          setErrors({
            general: t('login_validation_user_not_activated', 'Käyttäjä ei ole aktivoitu'),
            USER_NOT_ACTIVATED: true
          });
        } else {
          const errorMessage = 
            errorData?.detail || 
            errorData?.message || 
            errorData?.non_field_errors?.[0] || 
            t('general_error', 'Virhe. Ota tarvittaessa yhteyttä asiakaspalveluun');
          
          setErrors({general: errorMessage});
        }
      });
  },
  displayName: "LoginForm"
})(LoginForm);

export default LoginFormWithFormik;
